/**
 * Phase 6e.2 — pure FX-sync matcher.
 *
 *   buildFxSyncPlan(engagement, response) → FxSyncPlan
 *
 * No React. No hooks. No fetch. No store mutations. The Phase 6e.3
 * Magic Button is responsible for calling the HTTP client, passing
 * its response in here, and applying the returned plan to form
 * state. Keeping the brain pure makes every audit-relevant edge case
 * (T-1 fallback, unsupported currencies, MAX_FALLBACK_DAYS limit)
 * testable without spinning up the React tree.
 *
 * Per-FX-contract, the matcher fills three fields:
 *   1. `commencementSpotRate` — rate at `contract.commencementDate`.
 *   2. `historicalRates[]`     — one rate per month-start date the
 *                                existing `generateHistoricalRateDates`
 *                                helper enumerates (reused verbatim).
 *   3. `paymentDateRates[]`    — one rate per scheduled payment date
 *                                from `generatePaymentDates` (also
 *                                reused, no duplication).
 *
 * Engagement-level, the matcher fills `closingFxRates[ccy]` for
 * every distinct FX currency on `engagement.auditPeriodEnd`,
 * deduped — N USD contracts share ONE engagement-level USD closing
 * rate.
 *
 * `null` ratepoints + `errors[]` entries are first-class outputs.
 * The Magic Button decides how to surface them; the matcher just
 * faithfully reports.
 */

import { generateHistoricalRateDates } from '@/features/contract-form/utils/generateHistoricalRateDates';
import { generatePaymentDates } from '@/features/contract-form/utils/generatePaymentDates';
import type { ContractInput, EngagementHistoryEntry } from '@/types';

import { buildRatesIndex } from './buildRatesIndex';
import { lookupRate } from './lookupRate';
import type {
  ContractFxError,
  ContractFxUpdate,
  EngagementClosingRatesUpdate,
  FxRatesApiResponse,
  FxSyncAudit,
  FxSyncPlan,
  RateLookupResult,
  RatesByDate,
} from './types';

export function buildFxSyncPlan(
  engagement: EngagementHistoryEntry,
  response: FxRatesApiResponse,
): FxSyncPlan {
  const ratesIndex = buildRatesIndex(response);
  const unsupportedSet = new Set(
    response.data.unsupportedCurrencies.map((c) => c.toUpperCase()),
  );

  // ── Contract updates ──────────────────────────────────────────────────
  // Filter to live, non-deleted FX contracts. A `null` slot is an
  // empty placeholder the auditor hasn't filled yet.
  const fxContracts: ContractInput[] = [];
  for (const c of engagement.contracts) {
    if (c === null) continue;
    if (!c.currency) continue;
    if (c.currency === engagement.functionalCurrency) continue;
    fxContracts.push(c);
  }

  const contractUpdates: ContractFxUpdate[] = [];
  for (const contract of fxContracts) {
    contractUpdates.push(
      buildOneContractUpdate(contract, engagement, ratesIndex, unsupportedSet),
    );
  }

  // ── Engagement-level closing rates ────────────────────────────────────
  // Dedupe by currency — N USD contracts share ONE engagement closing
  // rate at `auditPeriodEnd`.
  const fxCurrencies = uniqueUppercase(fxContracts.map((c) => c.currency));
  const engagementClosingRates: EngagementClosingRatesUpdate[] = [];
  for (const ccy of fxCurrencies) {
    if (unsupportedSet.has(ccy)) {
      engagementClosingRates.push({
        currency: ccy,
        rate: null,
        auditPeriodEnd: engagement.auditPeriodEnd,
        error: {
          field: 'historicalRates',
          intendedDate: engagement.auditPeriodEnd,
          reason: 'CURRENCY_UNSUPPORTED',
          reasonI18nKey: 'fx.sync.error.currencyUnsupported',
          reasonParams: { currency: ccy },
        },
      });
      continue;
    }
    const rate = lookupRate(ratesIndex, engagement.auditPeriodEnd, ccy);
    if (rate) {
      engagementClosingRates.push({
        currency: ccy,
        rate,
        auditPeriodEnd: engagement.auditPeriodEnd,
      });
    } else {
      engagementClosingRates.push({
        currency: ccy,
        rate: null,
        auditPeriodEnd: engagement.auditPeriodEnd,
        error: {
          field: 'historicalRates',
          intendedDate: engagement.auditPeriodEnd,
          reason: 'NO_RATE_AVAILABLE',
          reasonI18nKey: 'fx.sync.error.noRateAvailable',
          reasonParams: { date: engagement.auditPeriodEnd, currency: ccy },
        },
      });
    }
  }

  // ── Audit summary ─────────────────────────────────────────────────────
  const audit = buildAudit(
    engagement,
    fxContracts.length,
    contractUpdates,
    engagementClosingRates,
    response,
  );

  return { contractUpdates, engagementClosingRates, audit };
}

// ─── Per-contract pass ────────────────────────────────────────────────────

function buildOneContractUpdate(
  contract: ContractInput,
  engagement: EngagementHistoryEntry,
  ratesIndex: RatesByDate,
  unsupportedSet: Set<string>,
): ContractFxUpdate {
  const ccy = (contract.currency ?? '').toUpperCase();

  if (unsupportedSet.has(ccy)) {
    // Whole-contract short-circuit. The matcher doesn't try the
    // individual dates — if TCMB has no series for this currency,
    // no date will match.
    return {
      contractId: contract.contractId,
      currency: ccy,
      commencementSpotRate: null,
      historicalRates: [],
      paymentDateRates: [],
      errors: [
        {
          field: 'commencementSpotRate',
          intendedDate: contract.commencementDate,
          reason: 'CURRENCY_UNSUPPORTED',
          reasonI18nKey: 'fx.sync.error.currencyUnsupported',
          reasonParams: { currency: ccy },
        },
      ],
    };
  }

  const errors: ContractFxError[] = [];

  // commencementSpotRate ─────────────────────────────────────────────
  const spot = lookupRate(ratesIndex, contract.commencementDate, ccy);
  if (!spot) {
    errors.push({
      field: 'commencementSpotRate',
      intendedDate: contract.commencementDate,
      reason: 'NO_RATE_AVAILABLE',
      reasonI18nKey: 'fx.sync.error.noRateAvailable',
      reasonParams: { date: contract.commencementDate, currency: ccy },
    });
  }

  // historicalRates — reuse the BB4 enumerator verbatim ─────────────
  const historicalDates = generateHistoricalRateDates({
    commencementDate: contract.commencementDate,
    leaseTermMonths: Number(contract.leaseTermMonths),
    auditPeriodEnd: engagement.auditPeriodEnd,
  });
  const historicalRates: Array<RateLookupResult & { intended: string }> = [];
  for (const intended of historicalDates) {
    const r = lookupRate(ratesIndex, intended, ccy);
    if (r) {
      historicalRates.push({ ...r, intended });
    } else {
      errors.push({
        field: 'historicalRates',
        intendedDate: intended,
        reason: 'NO_RATE_AVAILABLE',
        reasonI18nKey: 'fx.sync.error.noRateAvailable',
        reasonParams: { date: intended, currency: ccy },
      });
    }
  }

  // paymentDateRates — reuse `generatePaymentDates`. Phase 6e.4.2-hotfix
  // Bug B: the helper now requires `firstPaymentDate` (IFRS 16 §B34) as
  // the schedule anchor. If a history entry lacks one we bail out of
  // the payment-date branch — `ContractInput.payment.firstPaymentDate`
  // is required on the wire, so this is defensive against malformed
  // legacy entries rather than a typical path.
  const paymentDateRates: Array<RateLookupResult & { intended: string }> = [];
  if (contract.payment) {
    // MINI_FIX_BZ — mirror ContractTcmbSyncButton's proven fallback:
    // when `firstPaymentDate` is missing on the stored slot, anchor the
    // payment schedule at `commencementDate`. Previously the bulk
    // matcher skipped the entire payment-date branch when
    // `firstPaymentDate` was absent, leaving `paymentDateRates` empty and
    // causing the backend to reject calculation ("paymentDateRates array
    // is empty or absent"). The per-contract button never hit this
    // because its synthetic engagement already applies
    // `firstPaymentDate ?? commencementDate`. A well-formed
    // `firstPaymentDate` is still used verbatim — the fallback only fires
    // when it is missing — so there is no change for correct contracts.
    const anchorFirstPaymentDate =
      contract.payment.firstPaymentDate ?? contract.commencementDate;
    const paymentDates = generatePaymentDates({
      commencementDate: contract.commencementDate,
      firstPaymentDate: anchorFirstPaymentDate,
      leaseTermMonths: Number(contract.leaseTermMonths),
      frequency: contract.payment.frequency,
      timing: contract.payment.timing,
      auditPeriodEnd: engagement.auditPeriodEnd,
    });
    for (const intended of paymentDates) {
      const r = lookupRate(ratesIndex, intended, ccy);
      if (r) {
        paymentDateRates.push({ ...r, intended });
      } else {
        errors.push({
          field: 'paymentDateRates',
          intendedDate: intended,
          reason: 'NO_RATE_AVAILABLE',
          reasonI18nKey: 'fx.sync.error.noRateAvailable',
          reasonParams: { date: intended, currency: ccy },
        });
      }
    }
  }

  return {
    contractId: contract.contractId,
    currency: ccy,
    commencementSpotRate: spot,
    historicalRates,
    paymentDateRates,
    errors,
  };
}

// ─── Audit summary ────────────────────────────────────────────────────────

function buildAudit(
  engagement: EngagementHistoryEntry,
  fxContractsCount: number,
  contractUpdates: ContractFxUpdate[],
  engagementClosingRates: EngagementClosingRatesUpdate[],
  response: FxRatesApiResponse,
): FxSyncAudit {
  let totalRatesPlanned = 0;
  let totalRatesMatched = 0;
  let totalT1Fallbacks = 0;
  let totalUnmatched = 0;

  for (const u of contractUpdates) {
    // Currency-unsupported short-circuits emit no successful lookups
    // and one error per missing target — count the error, no planned.
    if (u.commencementSpotRate) {
      totalRatesPlanned += 1;
      if (u.commencementSpotRate.fellBackDays === 0) totalRatesMatched += 1;
      else totalT1Fallbacks += 1;
    }
    for (const r of u.historicalRates) {
      totalRatesPlanned += 1;
      if (r.fellBackDays === 0) totalRatesMatched += 1;
      else totalT1Fallbacks += 1;
    }
    for (const r of u.paymentDateRates) {
      totalRatesPlanned += 1;
      if (r.fellBackDays === 0) totalRatesMatched += 1;
      else totalT1Fallbacks += 1;
    }
    // Each error is one un-filled target. Count them as planned-and-
    // unmatched so `planned = matched + fallback + unmatched` holds.
    for (const _err of u.errors) {
      totalRatesPlanned += 1;
      totalUnmatched += 1;
    }
  }

  for (const e of engagementClosingRates) {
    totalRatesPlanned += 1;
    if (e.rate) {
      if (e.rate.fellBackDays === 0) totalRatesMatched += 1;
      else totalT1Fallbacks += 1;
    } else {
      totalUnmatched += 1;
    }
  }

  const firstRow = response.data.rows[0];
  const lastRow = response.data.rows[response.data.rows.length - 1];

  return {
    totalContracts: engagement.contracts.length,
    fxContracts: fxContractsCount,
    totalRatesPlanned,
    totalRatesMatched,
    totalT1Fallbacks,
    totalUnmatched,
    dateRangeRequested: {
      startDate: firstRow?.date ?? '',
      endDate: lastRow?.date ?? '',
    },
    unsupportedCurrencies: response.data.unsupportedCurrencies,
    source: response.data.source,
    fetchedAt: response.data.fetchedAt,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function uniqueUppercase(currencies: ReadonlyArray<string | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of currencies) {
    if (!c) continue;
    const upper = c.toUpperCase();
    if (seen.has(upper)) continue;
    seen.add(upper);
    out.push(upper);
  }
  return out;
}
